import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { standardTermSchema, type StandardTermFormValues } from '../schema/terminologySchema';
import type { StandardTerm } from '../types/terminology';
import { useStandardTerms } from '../hooks/useStandardTerms';
import { SimilarityBadge } from './SimilarityBadge';

const STATUS_OPTIONS = ['ACTIVE', 'DEPRECATED', 'PROPOSED'] as const;

interface StandardTermFormDialogProps {
  open: boolean;
  editTarget: StandardTerm | null; // null = create mode
  isLoading: boolean;
  errorMessage?: string;
  onClose: () => void;
  onSubmit: (values: StandardTermFormValues) => void;
}

// Simple debounce
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  outline: 'none',
  boxSizing: 'border-box',
};

const errorStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--spacing-1)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-1)',
};

const fieldStyle: React.CSSProperties = {
  marginBottom: 'var(--spacing-3)',
};

export function StandardTermFormDialog({
  open,
  editTarget,
  isLoading,
  errorMessage,
  onClose,
  onSubmit,
}: StandardTermFormDialogProps) {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<StandardTermFormValues>({
    resolver: zodResolver(standardTermSchema),
    defaultValues: {
      korean: '',
      englishShort: '',
      englishFull: '',
      description: '',
      domain: '',
      source: 'MANUAL',
      status: 'ACTIVE',
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (editTarget) {
        reset({
          korean: editTarget.korean,
          englishShort: editTarget.englishShort,
          englishFull: editTarget.englishFull ?? '',
          description: editTarget.description ?? '',
          domain: editTarget.domain ?? '',
          source: editTarget.source ?? 'MANUAL',
          status: editTarget.status ?? 'ACTIVE',
        });
      } else {
        reset({
          korean: '',
          englishShort: '',
          englishFull: '',
          description: '',
          domain: '',
          source: 'MANUAL',
          status: 'ACTIVE',
        });
      }
    }
  }, [open, editTarget, reset]);

  // Similar terms preview — debounce keyword from korean/englishShort
  const koreanWatch = watch('korean');
  const englishShortWatch = watch('englishShort');
  const previewKeyword = koreanWatch || englishShortWatch;
  const debouncedKeyword = useDebounced(previewKeyword, 400);

  const similarQuery = useStandardTerms(
    debouncedKeyword.trim().length >= 2
      ? { keyword: debouncedKeyword.trim(), size: 3 }
      : {}
  );
  const showSimilar =
    debouncedKeyword.trim().length >= 2 &&
    (similarQuery.data?.content?.length ?? 0) > 0;

  const title = editTarget
    ? t('terminologyManagement.term.form.title.edit', { id: editTarget.id })
    : t('terminologyManagement.term.form.title.create');

  return (
    <FormDialog
      open={open}
      title={title}
      onClose={onClose}
      onCancel={onClose}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={isLoading ? t('terminologyManagement.form.button.loading') : t('terminologyManagement.form.button.submit')}
      cancelLabel={t('terminologyManagement.form.button.cancel')}
      submitTestId="term-form-submit"
    >
      <div style={{ minWidth: 420, maxWidth: 560 }}>
        {/* Inline error */}
        {errorMessage && (
          <div
            role="alert"
            aria-live="assertive"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background: 'var(--color-danger-light, #fef2f2)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* korean */}
        <div style={fieldStyle}>
          <label htmlFor="term-korean" style={labelStyle}>
            {t('terminologyManagement.term.form.field.korean')}{' '}
            <span aria-hidden="true" style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <input
            id="term-korean"
            type="text"
            autoComplete="off"
            aria-required="true"
            aria-invalid={!!errors.korean}
            aria-describedby={errors.korean ? 'term-korean-error' : undefined}
            {...register('korean')}
            style={inputStyle}
          />
          {errors.korean && (
            <p id="term-korean-error" style={errorStyle} role="alert">
              {errors.korean.message}
            </p>
          )}
        </div>

        {/* englishShort */}
        <div style={fieldStyle}>
          <label htmlFor="term-eng-short" style={labelStyle}>
            {t('terminologyManagement.term.form.field.englishShort')}{' '}
            <span aria-hidden="true" style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <input
            id="term-eng-short"
            type="text"
            autoComplete="off"
            aria-required="true"
            aria-invalid={!!errors.englishShort}
            aria-describedby="term-eng-short-hint"
            placeholder="CK01_CTLNO"
            {...register('englishShort')}
            style={inputStyle}
          />
          <p id="term-eng-short-hint" style={{ ...errorStyle, color: 'var(--color-text-secondary)' }}>
            {t('terminologyManagement.term.form.field.englishShort.hint')}
          </p>
          {errors.englishShort && (
            <p style={errorStyle} role="alert">
              {errors.englishShort.message}
            </p>
          )}
        </div>

        {/* englishFull */}
        <div style={fieldStyle}>
          <label htmlFor="term-eng-full" style={labelStyle}>
            {t('terminologyManagement.term.form.field.englishFull')}
          </label>
          <input
            id="term-eng-full"
            type="text"
            autoComplete="off"
            {...register('englishFull')}
            style={inputStyle}
          />
          {errors.englishFull && (
            <p style={errorStyle} role="alert">
              {errors.englishFull.message}
            </p>
          )}
        </div>

        {/* description */}
        <div style={fieldStyle}>
          <label htmlFor="term-description" style={labelStyle}>
            {t('terminologyManagement.term.form.field.description')}
          </label>
          <textarea
            id="term-description"
            rows={2}
            {...register('description')}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
          {errors.description && (
            <p style={errorStyle} role="alert">
              {errors.description.message}
            </p>
          )}
        </div>

        {/* domain */}
        <div style={fieldStyle}>
          <label htmlFor="term-domain" style={labelStyle}>
            {t('terminologyManagement.term.form.field.domain')}
          </label>
          <input
            id="term-domain"
            type="text"
            {...register('domain')}
            style={inputStyle}
          />
        </div>

        {/* source + status — side by side */}
        <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-3)' }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="term-source" style={labelStyle}>
              {t('terminologyManagement.term.form.field.source')}
            </label>
            <input
              id="term-source"
              type="text"
              {...register('source')}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="term-status" style={labelStyle}>
              {t('terminologyManagement.term.form.field.status')}
            </label>
            <select
              id="term-status"
              {...register('status')}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {t(`terminologyManagement.status.${s}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Similar terms preview */}
        {showSimilar && (
          <div
            style={{
              marginTop: 'var(--spacing-2)',
              padding: 'var(--spacing-3)',
              background: 'var(--color-surface-muted)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
            }}
            aria-live="polite"
          >
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-2)',
              }}
            >
              {t('terminologyManagement.term.form.similarPreview.title')}
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {similarQuery.data?.content.map((term) => (
                <li
                  key={term.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--spacing-1)',
                  }}
                >
                  <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                    {term.korean}
                  </span>
                  <span style={{ color: 'var(--color-text-muted)' }}>({term.englishShort})</span>
                  <SimilarityBadge similarity={term.similarity} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </FormDialog>
  );
}
